import { ReactNode, useEffect } from "react";
import { useShallow } from "zustand/shallow";

import { useBoundStore } from "@/app/store";
import { storage } from "@/shared/utils/local-storage";
import { isStoreCreated } from "@/shared/utils/indexdb";

interface StoreHandlerProps {
  children?: ReactNode;
}

export function StoreHandler({ children }: StoreHandlerProps) {
  const { fetchDeletedNotes, fetchNotes, fetchFolders, fetchTags, fetchTodos, removeHardDeletedNotes } = useBoundStore(
    useShallow((state) => ({
      fetchDeletedNotes: state.notes.fetchDeletedNotes,
      fetchNotes: state.notes.fetchNotes,
      fetchFolders: state.folders.fetchFolders,
      fetchTags: state.tags.fetchTags,
      fetchTodos: state.todos.fetchTodos,
      removeHardDeletedNotes: state.notes.removeHardDeletedNotes,
    }))
  );
  const isActive = useBoundStore(useShallow((state) => state.device.isActive));
  const isAuthenticated = useBoundStore(useShallow((state) => state.user.isAuthenticated));
  const dbLocked = useBoundStore(useShallow((state) => state.user.dbLocked));
  const setUser = useBoundStore(useShallow((state) => state.user.set));

  useEffect(() => {
    (async () => {
      if (!isAuthenticated || dbLocked) {
        return;
      }

      if (!isStoreCreated()) {
        setUser({ storeInitialized: false });
        // Wait for all fetched to be initialized
        await Promise.all([fetchDeletedNotes(), fetchNotes(true), fetchTodos(), fetchTags(), fetchFolders()]);

        setUser({ storeInitialized: true });
      } else if (isActive) {
        // Fetch updated data
        await Promise.all([fetchDeletedNotes(), fetchNotes(), fetchTodos(), fetchTags(), fetchFolders()]);
      }

      await removeHardDeletedNotes();
      storage.setLastSync(new Date().toISOString());
    })();
  }, [
    isAuthenticated,
    dbLocked,
    isActive,
    fetchDeletedNotes,
    fetchNotes,
    fetchFolders,
    fetchTags,
    fetchTodos,
    removeHardDeletedNotes,
    setUser,
  ]);

  return children;
}
